import { useEffect, useState } from "react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function QuickPlaysAdmin({ api }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tests, setTests] = useState(null);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [d, s] = await Promise.all([api.get("/admin/quick-plays"), api.get("/admin/settings")]);
    setData(d.data); setSettings(s.data);
  };
  useEffect(() => { load().catch(() => setMsg("Failed to load")); }, []);

  const savePrice = async (slug, price_cents, active, display_order) => {
    await api.post(`/admin/quick-plays/settings/${slug}`, { price_cents, active, display_order });
    setMsg("Saved."); load();
  };
  const [usage, setUsage] = useState(null);
  const [cap, setCap] = useState("");
  useEffect(() => { api.get("/admin/scout/usage").then((r) => { setUsage(r.data); setCap(String(r.data.cap_usd)); }).catch(() => {}); }, []);
  const saveCap = async () => {
    try { await api.post("/admin/settings", { scout_daily_cap_usd: Number(cap) }); const r = await api.get("/admin/scout/usage"); setUsage(r.data); setMsg("Scout cap saved."); }
    catch { setMsg("Cap must be between $0 and $500."); }
  };
  const toggleScreening = async (v) => {
    await api.post("/admin/settings", { scout_safety_screening: v });
    setSettings((s) => ({ ...s, scout_safety_screening: v }));
  };
  const action = async (id, kind) => {
    setMsg("Working…");
    await api.post(`/admin/quick-plays/orders/${id}/${kind}`);
    setMsg(`${kind} done.`); load();
  };
  const runTests = async () => {
    setTesting(true); setMsg("Running safety tests (this calls the classifier many times)…");
    try { const { data } = await api.get("/admin/quick-plays/safety-tests"); setTests(data); setMsg(""); }
    catch { setMsg("Test run failed."); } finally { setTesting(false); }
  };

  if (!data) return <p className="text-[#6E6E6E]">{msg || "Loading…"}</p>;
  const m = data.metrics;

  return (
    <div className="space-y-8" data-testid="quick-plays-admin">
      {msg && <p className="text-sm text-[#DB9130]">{msg}</p>}

      {/* Scout safety toggle */}
      <div className="border-2 border-[#D3CFC0] p-4 flex items-center justify-between">
        <div>
          <p className="font-head uppercase text-[#1E1E1E]">Scout Safety Screening</p>
          <p className="text-sm text-[#6E6E6E]">When ON, every Scout message is pre-screened and escalations show inline. OFF = Scout behaves as before.</p>
        </div>
        <button data-testid="toggle-scout-screening" onClick={() => toggleScreening(!settings?.scout_safety_screening)}
          className={`px-4 py-2 font-head uppercase text-sm border-2 ${settings?.scout_safety_screening ? "bg-[#1E1E1E] text-[#F3F1EC] border-[#1E1E1E]" : "border-[#D3CFC0] text-[#6E6E6E]"}`}>
          {settings?.scout_safety_screening ? "ON" : "OFF"}
        </button>
      </div>

      <div className="border-2 border-[#D3CFC0] p-4 space-y-3" data-testid="scout-usage-panel">
        <p className="font-head uppercase text-[#1E1E1E]">Scout daily spend cap</p>
        <p className="text-sm text-[#6E6E6E]">When today's estimated AI spend reaches the cap, Scout replies "Scout's resting — try again tomorrow" instead of calling the AI.</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">$</span>
          <input data-testid="scout-cap-input" type="number" min={0} max={500} step="0.5" value={cap} onChange={(e) => setCap(e.target.value)} className="w-24 border-2 border-[#D3CFC0] px-2 py-1 text-sm" />
          <span className="text-sm text-[#6E6E6E]">per day</span>
          <button data-testid="scout-cap-save" onClick={saveCap} className="px-3 py-1 border-2 border-[#1E1E1E] font-head uppercase text-xs">Save</button>
        </div>
        {usage && (
          <div className="text-xs font-[IBM_Plex_Mono] text-[#1E1E1E] space-y-0.5">
            <p data-testid="scout-usage-today">Today: ${usage.today_usd.toFixed(2)} of ${usage.cap_usd.toFixed(2)}</p>
            {usage.days.map((d) => <p key={d.date} className="text-[#6E6E6E]">{d.date} · {d.calls} AI calls · ${d.usd.toFixed(2)}</p>)}
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qp-metrics">
        {[["Purchases", m.purchases], ["Completed", m.completed], ["Revenue", money(m.revenue_cents)],
          ["Needs attention", m.needs_attention], ["Escalations", m.escalated], ["LLM cost", money(Math.round(m.est_llm_cost_cents))],
          ["Stripe fees", money(m.est_stripe_fees_cents)], ["Margin", money(m.est_margin_cents)]].map(([k, v]) => (
          <div key={k} className="border-2 border-[#D3CFC0] p-3">
            <p className="text-xs uppercase text-[#6E6E6E] font-[IBM_Plex_Mono]">{k}</p>
            <p className="text-2xl font-head text-[#1E1E1E]">{v}</p>
          </div>
        ))}
      </div>

      {/* Plays: price/active/order editable */}
      <div>
        <h3 className="font-head uppercase text-[#1E1E1E] mb-2">Quick Plays</h3>
        {data.plays.map((p) => (
          <PlayRow key={p.slug} play={p} onSave={savePrice} />
        ))}
      </div>

      {/* Orders */}
      <div>
        <h3 className="font-head uppercase text-[#1E1E1E] mb-2">Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-[#6E6E6E] font-[IBM_Plex_Mono] border-b-2 border-[#1E1E1E]">
              <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Play</th><th className="py-2 pr-3">Price</th>
              <th className="py-2 pr-3">Att</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Cost</th><th className="py-2">Actions</th></tr></thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E1D6]" data-testid={`qp-order-${o.id}`}>
                  <td className="py-2 pr-3"><span className={o.status === "needs_attention" ? "text-[#C0492B] font-bold" : "text-[#1E1E1E]"}>{o.status}</span>
                    {o.status === "needs_attention" && o.needs_attention_since && <span className="block text-xs text-[#C0492B]">since {new Date(o.needs_attention_since).toLocaleString()}</span>}</td>
                  <td className="py-2 pr-3">{o.slug}</td><td className="py-2 pr-3">{money(o.price_cents)}</td>
                  <td className="py-2 pr-3">{o.generation_attempts ?? "-"}</td><td className="py-2 pr-3">{o.email_status || "-"}</td>
                  <td className="py-2 pr-3">{money(Math.round(o.est_cost_cents || 0))}</td>
                  <td className="py-2 space-x-2 whitespace-nowrap">
                    {o.status === "needs_attention" && <button onClick={() => action(o.id, "rerun")} className="underline text-[#DB9130]">rerun</button>}
                    {o.status === "completed" && <button onClick={() => action(o.id, "resend")} className="underline text-[#1E1E1E]">resend</button>}
                    {["completed", "needs_attention"].includes(o.status) && !o.refunded && <button onClick={() => action(o.id, "refund")} className="underline text-[#C0492B]">refund</button>}
                  </td>
                </tr>
              ))}
              {!data.orders.length && <tr><td colSpan="7" className="py-4 text-[#6E6E6E]">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Safety tests */}
      <div>
        <button data-testid="run-safety-tests" onClick={runTests} disabled={testing}
          className="px-5 py-2 font-head uppercase text-sm bg-[#1E1E1E] text-[#F3F1EC] border-2 border-[#1E1E1E] disabled:opacity-50">
          {testing ? "Running…" : "Run safety tests"}
        </button>
        {tests && (
          <div className="mt-3 border-2 border-[#D3CFC0] p-4" data-testid="safety-test-results">
            <p className="text-[#1E1E1E]">Safety: <strong>{tests.safety.passed}/{tests.safety.total}</strong> escalation cases correct ·
              Normal Scout: <strong>{tests.normal.false_positives}/{tests.normal.count}</strong> false positives
              (<strong>{tests.normal.false_positive_rate}%</strong>)</p>
            <div className="mt-2 max-h-64 overflow-y-auto text-xs">
              {tests.results.filter((r) => !r.pass).map((r, i) => (
                <div key={i} className="text-[#C0492B]">✗ [{r.type}] expected {r.expected}, got {r.got}: {r.text}</div>
              ))}
              {tests.results.every((r) => r.pass) && <p className="text-green-700">All cases passed.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayRow({ play, onSave }) {
  const [price, setPrice] = useState((play.price_cents / 100).toFixed(2));
  const [active, setActive] = useState(play.active);
  const [order, setOrder] = useState(play.display_order);
  return (
    <div className="border-2 border-[#D3CFC0] p-3 mb-2 flex flex-wrap items-center gap-3" data-testid={`play-row-${play.slug}`}>
      <span className="font-head text-[#1E1E1E] w-48">QP{play.number} · {play.title}</span>
      <label className="text-sm text-[#6E6E6E]">Price $
        <input value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`price-${play.slug}`}
          className="ml-1 w-20 border-2 border-[#D3CFC0] px-2 py-1" /></label>
      <label className="text-sm text-[#6E6E6E]">Order
        <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))}
          className="ml-1 w-16 border-2 border-[#D3CFC0] px-2 py-1" /></label>
      <label className="text-sm text-[#6E6E6E] flex items-center gap-1">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>
      <button onClick={() => onSave(play.slug, Math.round(parseFloat(price) * 100), active, order)}
        data-testid={`save-${play.slug}`} className="px-3 py-1 bg-[#1E1E1E] text-[#F3F1EC] font-head uppercase text-xs">Save</button>
    </div>
  );
}
